/**
 * LocalAttachmentCache — in-memory blob-URL cache keyed by the final HTTPS
 * URL that appears in note markdown.
 *
 * Why this exists:
 *   - The client picks an attachment id up front and bakes the final URL
 *     (`${API}/attachments/<id>/download`) into the markdown immediately.
 *   - Before the upload completes, that URL returns 404. If the browser
 *     rendered the raw <img src> it would flash a broken icon.
 *   - Instead, the editor looks up the URL here. If we primed it with the
 *     local file blob, the editor gets a `blob:` URL and shows the image
 *     instantly. After the upload completes, we release the blob and the
 *     browser falls back to fetching the HTTPS URL normally.
 *
 * This module is web-only. The Swift app has its own cache
 * (`AttachmentImageCache`) with identical semantics.
 */

import { Sentry } from './sentry'

const entries = new Map<string, string>()

/** Event name broadcast whenever a URL's bytes become (or stop being) available. */
const EVENT = 'caliu-local-attachment-cache-updated'

/**
 * Resolves bytes for a URL this cache has never seen, injected so the module
 * stays DB-free. It exists because the in-memory map dies with the page: with
 * no account the upload queue holds the only copy of the attachment, so
 * without a rehydrate every not-yet-uploaded image 404s after a reload.
 */
type Fallback = (url: string) => { fileData: string; contentType: string } | undefined
let fallback: Fallback | null = null
/**
 * URLs the fallback has already answered for, hit or miss. `getLocalAttachment`
 * is the editor's render hot path, so each URL costs at most one query for the
 * life of the page.
 */
const fallbackChecked = new Set<string>()

export function setLocalAttachmentFallback(resolver: Fallback): void {
  fallback = resolver
}

function store(url: string, file: Blob): string {
  const existing = entries.get(url)
  if (existing) URL.revokeObjectURL(existing)
  const blobUrl = URL.createObjectURL(file)
  entries.set(url, blobUrl)
  return blobUrl
}

export function primeLocalAttachment(url: string, file: Blob): void {
  store(url, file)
  notify(url)
}

export function getLocalAttachment(url: string): string | undefined {
  const cached = entries.get(url)
  if (cached) return cached
  if (!fallback || fallbackChecked.has(url)) return undefined
  fallbackChecked.add(url)
  let queued: ReturnType<Fallback>
  try {
    queued = fallback(url)
  } catch (err) {
    // The resolver is a SQLite query and this runs inside a render, so an
    // unhandled throw takes the editor down with it. One report, then treat
    // the URL as a miss forever (`fallbackChecked` is already set).
    Sentry.captureException(err)
    return undefined
  }
  if (!queued) return undefined
  // Decoded synchronously (`atob`, not the async `base64ToBlob` helper): this
  // is called from a render, and returning undefined now would paint the
  // broken URL before any promise could resolve.
  const bytes = base64ToBytes(queued.fileData)
  if (!bytes) return undefined
  // `store`, not `primeLocalAttachment`: notifying here would dispatch a DOM
  // event mid-render, and its subscribers set React state. The caller gets the
  // blob URL as the return value, so nobody needs the event on this path.
  return store(url, new Blob([bytes], { type: queued.contentType }))
}

function base64ToBytes(base64: string): ArrayBuffer | undefined {
  try {
    const binary = atob(base64)
    const buffer = new ArrayBuffer(binary.length)
    const bytes = new Uint8Array(buffer)
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
    return buffer
  } catch {
    // A corrupt row must not take the editor down with it.
    return undefined
  }
}

export function releaseLocalAttachment(url: string): void {
  // The upload landed, so the queue row is gone too: never rehydrate this URL
  // again, or the next render resurrects the blob from a row that no longer
  // exists (and pays a query to find that out).
  fallbackChecked.add(url)
  const existing = entries.get(url)
  if (!existing) return
  URL.revokeObjectURL(existing)
  entries.delete(url)
  notify(url)
}

export function subscribeLocalAttachment(
  listener: (url: string) => void,
): () => void {
  const handler = (e: Event) => {
    const detail = (e as CustomEvent<string>).detail
    if (typeof detail === 'string') listener(detail)
  }
  window.addEventListener(EVENT, handler as EventListener)
  return () => window.removeEventListener(EVENT, handler as EventListener)
}

function notify(url: string): void {
  window.dispatchEvent(new CustomEvent(EVENT, { detail: url }))
}
